import React, { useState } from 'react';
import _get from 'lodash/get';
import { useData } from 'resift';
import MappingProps, * as MappingTypes from 'types/Mapping';
import SourceType from 'types/Source';
import MappingsContext from 'helpers/MappingsContext';
import makeGetMappingsAttributes from 'fetches/makeGetMappingsAttributes';
import {
  PRIMARY_MAPPING_TYPE_KEY,
  ORG_MAPPING_TYPE_KEY,
  PROFILE_ATTR_MAPPING_TYPE_KEY,
} from 'pages/Directory/consts';

import Card from '@material-ui/core/Card';
import PrimaryKeyMapping from 'components/Mappings/PrimaryKeyMapping';
import OrgMapping from 'components/Mappings/OrgMapping';
import ProfileAttrMapping from 'components/Mappings/ProfileAttrMapping';

interface Props {
  cardData: MappingTypes.MappingCardData[];
  selectedDestinations: string[];
  onUpdateState: (cardIndex: number, newMappings: MappingProps[]) => void;
  source: SourceType;
}

function MappingCards({ cardData, selectedDestinations, onUpdateState, source }: Props) {
  const getMappingsAttributes = makeGetMappingsAttributes(source.id);
  const mappingAttributes = useData(getMappingsAttributes);
  const selectionAttributesArray: MappingTypes.SelectionAttribute[] = _get(
    mappingAttributes,
    'selectionAttributes',
    [],
  );
  const _sourceAttrOptions = selectionAttributesArray.map(attr => attr.sourcePath);
  const [sourceAttrOptions, setSourceAttrOptions] = useState(_sourceAttrOptions);

  const handleDeleteMapping = (cardIndex: number, mappings: MappingProps[]) => {
    return (indexToDelete: number) => {
      const newMappings = [...mappings];
      newMappings.splice(indexToDelete, 1);
      onUpdateState(cardIndex, newMappings);
    };
  };

  const handleUpdateMapping = (cardIndex: number, mappings: MappingProps[]) => {
    return (newMappingIndex: number, newMapping: MappingProps) => {
      const newMappings = [...mappings];
      newMappings.splice(newMappingIndex, 1, newMapping);
      onUpdateState(cardIndex, newMappings);
    };
  };

  const handleUpdateCard = (cardIndex: number) => {
    return (newMappings: MappingProps[]) => {
      onUpdateState(cardIndex, newMappings);
    };
  };

  const handleAddCustomSourceAttribute = (newOption: string) => {
    setSourceAttrOptions([...sourceAttrOptions, newOption]);
  };

  const mappingContext: MappingTypes.MappingContext = {
    source,
    selectedDestinations,
    sourceAttrOptions,
    onAddCustomSourceAttribute: handleAddCustomSourceAttribute,
  };

  return (
    <MappingsContext.Provider value={mappingContext}>
      {cardData.map((data, index) => {
        const { mappings, type } = data;

        let CardComponent = null;

        switch (type) {
          case PRIMARY_MAPPING_TYPE_KEY:
            CardComponent = PrimaryKeyMapping;
            break;
          case ORG_MAPPING_TYPE_KEY:
            CardComponent = OrgMapping;
            break;
          case PROFILE_ATTR_MAPPING_TYPE_KEY:
            CardComponent = ProfileAttrMapping;
            break;
        }

        return (
          <div key={`mappings-card-${index}`}>
            {!CardComponent ? null : (
              <Card style={{ marginBottom: 16, overflow: 'initial' }}>
                <CardComponent
                  mappings={mappings}
                  key={index}
                  onDeleteMapping={handleDeleteMapping(index, mappings)}
                  onUpdateMapping={handleUpdateMapping(index, mappings)}
                  onUpdateCard={handleUpdateCard(index)}
                />
              </Card>
            )}
          </div>
        );
      })}
    </MappingsContext.Provider>
  );
}

export default MappingCards;
